import type { FC } from 'react'
import React from 'react'
import type { ReactMarkdownOptions } from 'react-markdown'
import ReactMarkdown from 'react-markdown'
import { Accordion } from './Accordion'
import { ReactNode } from 'react'
import { Typography } from 'antd'

/**
 * A functional component that renders Markdown content.
 * This component customizes how certain Markdown elements are rendered
 * by providing specific components for each supported Markdown element.
 *
 * The component is configured to render the following Markdown elements with custom components:
 * - h1: Rendered with MarkdownH1Tag
 * - h2: Rendered with MarkdownH2Tag
 * - h3: Rendered with MarkdownH3Tag
 * - h4: Rendered with MarkdownH4Tag
 * - li: Rendered with MarkdownLiTag
 * - ol: Rendered with MarkdownOlTag
 * - p: Rendered with MarkdownPTag
 * - ul: Rendered with MarkdownUlTag
 
 * - Supports custom collapsible sections (accordion) using the `Accordion.tsx` component. Syntax:
 * ::: Question
 * Answer
 * :::
 */
export const Markdown: FC<
	ReactMarkdownOptions & { children: React.ReactNode; skipAccordion?: boolean }
> = ({ children, skipAccordion = false, ...props }) => {
	const text =
		typeof children === 'string'
			? children
			: Array.isArray(children) &&
				  (children as unknown[]).every(
						(child: unknown) => typeof child === 'string'
				  )
				? (children as string[]).join('')
				: undefined

	if (!skipAccordion && typeof text === 'string' && text.includes(':::')) {
		const regex = /:::\s+([^\n]+?)\n([\s\S]*?):::/g
		let lastIndex = 0
		const elements: ReactNode[] = []
		let match
		let key = 0

		while ((match = regex.exec(text)) !== null) {
			if (match.index > lastIndex) {
				const before = text.slice(lastIndex, match.index)
				if (before.trim()) {
					elements.push(
						<Markdown skipAccordion key={key++}>
							{before}
						</Markdown>
					)
				}
			}
			elements.push(
				<Accordion
					key={key++}
					items={[
						{
							title: match[1].trim(),
							content: <Markdown skipAccordion>{match[2]}</Markdown>
						}
					]}
				/>
			)
			lastIndex = regex.lastIndex
		}

		if (lastIndex < text.length) {
			const after = text.slice(lastIndex)
			if (after.trim()) {
				elements.push(
					<Markdown skipAccordion key={key++}>
						{after}
					</Markdown>
				)
			}
		}

		return <>{elements}</>
	}

	const MarkdownPTag: FC<{ children: ReactNode }> = ({
		children,
		...props
	}) => <Typography.Paragraph {...props}>{children}</Typography.Paragraph>

	const MarkdownH1Tag: FC<{ children: React.ReactNode }> = ({
		children,
		...props
	}) => (
		<Typography.Title level={1} {...props}>
			{children}
		</Typography.Title>
	)

	const MarkdownH2Tag: FC<{ children: React.ReactNode }> = ({
		children,
		...props
	}) => (
		<Typography.Title level={2} {...props}>
			{children}
		</Typography.Title>
	)

	const MarkdownH3Tag: FC<{ children: React.ReactNode }> = ({
		children,
		...props
	}) => (
		<Typography.Title level={3} {...props}>
			{children}
		</Typography.Title>
	)

	const MarkdownH4Tag: FC<{ children: React.ReactNode }> = ({
		children,
		...props
	}) => (
		<Typography.Title level={4} {...props}>
			{children}
		</Typography.Title>
	)

	const MarkdownUlTag: FC<{ children: React.ReactNode }> = ({
		children,
		...props
	}) => <ul {...props}>{children}</ul>

	const MarkdownOlTag: FC<{ children: React.ReactNode }> = ({
		children,
		...props
	}) => <ol {...props}>{children}</ol>

	const MarkdownLiTag: FC<{ children: React.ReactNode }> = ({
		children,
		...props
	}) => (
		<li>
			<Typography {...props}>{children}</Typography>
		</li>
	)

	const markdownComponentConfiguration = {
		h1: MarkdownH1Tag,
		h2: MarkdownH2Tag,
		h3: MarkdownH3Tag,
		h4: MarkdownH4Tag,
		li: MarkdownLiTag,
		ol: MarkdownOlTag,
		p: MarkdownPTag,
		ul: MarkdownUlTag
	}

	return (
		<ReactMarkdown components={markdownComponentConfiguration} {...props}>
			{children}
		</ReactMarkdown>
	)
}
